import React from "react";
import { Checkbox, Modal, Select, Tag, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import NumberInput from "../common/NumberInput";
import { BaseAttachmentCode, FootpathColumnKey, FootpathConstructionType, FootpathLayerConfig, FootpathsConstructionConfig } from "../../types/tender.types";
import { BASE_ATTACHMENT_NONE, BASE_ATTACHMENT_OPTIONS, BASE_COURSE_OPTIONS, BINDER_COURSE_OPTIONS, COLORS } from "../../utils/constants";


export const DEFAULT_FOOTPATHS_CONFIG: FootpathsConstructionConfig = {
  tarmacPath: {
    membrane: false,
    capping: 0,
    subBase: 0.15,
    base: 0,
    binder: 0.075,
    surface: 0.025,
    constructionType: "AC6 DBM",
    topsoil: 0,
  },
  tarmacCrossover: {
    membrane: false,
    capping: 0,
    subBase: 0.2,
    base: 0.1,
    binder: 0.06,
    surface: 0.025,
    constructionType: "AC6 DBM",
    topsoil: 0,
  },
  heavyDutyCrossover: {
    membrane: false,
    capping: 0,
    subBase: 0,
    base: 0,
    binder: 0,
    surface: 0,
    constructionType: "AC6 DBM",
    topsoil: 0,
  },
  blockPavedCrossover: {
    membrane: false,
    capping: 0,
    subBase: 0,
    base: 0,
    binder: 0,
    surface: 0,
    constructionType: "80mm Block Paving",
    topsoil: 0,
  },
  cycleway: {
    membrane: false,
    capping: 0,
    subBase: 0,
    base: 0,
    binder: 0,
    surface: 0,
    constructionType: "AC6 DBM",
    topsoil: 0,
  },
  verge: {
    membrane: false,
    capping: 0,
    subBase: 0,
    base: 0,
    binder: 0,
    surface: 0,
    constructionType: "site won topsoil",
    topsoil: 0.15,
  },
};

const TARMAC_CONSTRUCTION_TYPES: FootpathConstructionType[] = ["AC6 DBM", "AC10 DBM", "HRA", "SMA"];

const BLOCK_PAVED_CONSTRUCTION_TYPES: FootpathConstructionType[] = [
  "60mm Block Paving",
  "80mm Block Paving",
  "60mm Tegula Paving",
  "80mm Tegula Paving",
  "60mm Keyblok",
  "80mm Keyblok",
];

const VERGE_CONSTRUCTION_TYPES: FootpathConstructionType[] = [
  "site won topsoil",
  "imported topsoil",
];

const COLUMN_KEYS: Array<FootpathColumnKey> = [
  "tarmacPath",
  "tarmacCrossover",
  "heavyDutyCrossover",
  "blockPavedCrossover",
  "cycleway",
  "verge",
];

const COLUMN_LABELS: Record<FootpathColumnKey, string> = {
  tarmacPath: "Tarmac Path",
  tarmacCrossover: "Tarmac Crossover",
  heavyDutyCrossover: "Heavy Duty Crossover",
  blockPavedCrossover: "Block Paved & Crossover",
  cycleway: "Cycleway",
  verge: "Verge",
};

function calcTotal(col: FootpathLayerConfig): number {
  return (
    (col.capping || 0) +
    (col.subBase || 0) +
    (col.base || 0) +
    (col.binder || 0) +
    (col.surface || 0) +
    (col.topsoil || 0)
  );
}

// Matches Excel: AVERAGEIF(BJ12:BO12,">0") — averages only the column Totals greater than 0.
export function calculateFootpathsAverageBuildUp(
  config: FootpathsConstructionConfig
): number {
  const positiveTotals = COLUMN_KEYS
    .map((key) => calcTotal(config[key]))
    .filter((total) => total > 0);
  return positiveTotals.length > 0
    ? positiveTotals.reduce((sum, total) => sum + total, 0) / positiveTotals.length
    : 0;
}

interface FootpathsConfigModalProps {
  open: boolean;
  onClose: () => void;
  /** Called on the X icon/mask click/Esc, as distinct from "Done" (`onClose`) —
   * lets the caller revert live-bound field edits the modal has no draft of its own to discard.
   * Defaults to `onClose` when omitted. */
  onCancel?: () => void;
  footpathsConfig: FootpathsConstructionConfig;
  onUpdate: (config: FootpathsConstructionConfig) => void;
  isViewMode?: boolean;
}

const FootpathsConfigModal: React.FC<FootpathsConfigModalProps> = ({
  open,
  onClose,
  onCancel,
  footpathsConfig,
  onUpdate,
  isViewMode = false,
}) => {
  const courseConfig = footpathsConfig;
  const onCourseConfigUpdate = (patch: Partial<FootpathsConstructionConfig>) => {
    if (isViewMode) return;
    onUpdate({ ...footpathsConfig, ...patch });
  };
  const renderCourseLabel = (
    title: string,
    typeField: "baseCourseType" | "binderCourseType",
    attachField: "baseAttachments" | "binderAttachments",
    options: readonly { c: string; n: string }[],
  ) => (
    <td style={{ ...labelStyle, padding: "2px 4px", whiteSpace: "normal" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 44, flexShrink: 0 }}>{title}</span>
          <Select
            value={courseConfig[typeField] ?? ""}
            onChange={(v: string) => onCourseConfigUpdate?.({ [typeField]: v || undefined })}
            size="small"
            style={{ flex: 1, minWidth: 0 }}
          >
            <Select.Option value="">Select</Select.Option>
            {options.map((o) => (
              <Select.Option key={o.c} value={o.n}>{o.n}</Select.Option>
            ))}
          </Select>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 44, flexShrink: 0 }}>Attach</span>
          <Select
            mode="multiple"
            size="small"
            placeholder="Select"
            value={(courseConfig[attachField] ?? []).filter((v) => v !== BASE_ATTACHMENT_NONE)}
            onChange={(vals: BaseAttachmentCode[]) => {
              const picked = vals.filter((v) => v !== BASE_ATTACHMENT_NONE);
              onCourseConfigUpdate?.({ [attachField]: picked.length > 0 ? picked : undefined });
            }}
            options={BASE_ATTACHMENT_OPTIONS.map((o) => ({ value: o.c, label: o.n }))}
            optionRender={(opt) => (
              <Checkbox
                checked={(courseConfig[attachField] ?? []).includes(opt.value as BaseAttachmentCode)}
                style={{ pointerEvents: "none" }}
              >
                {opt.label}
              </Checkbox>
            )}
            menuItemSelectedIcon={null}
            style={{ flex: 1, minWidth: 0 }}
          />
        </div>
      </div>
    </td>
  );

  const updateCol = (
    key: FootpathColumnKey,
    field: keyof FootpathLayerConfig,
    value: boolean | number | string,
  ) => {
    if (isViewMode) return;
    onUpdate({ ...footpathsConfig, [key]: { ...footpathsConfig[key], [field]: value } });
  };

  const thStyle: React.CSSProperties = {
    backgroundColor: COLORS.tableHeaderBg,
    border: "1px solid #e5e7eb",
    padding: "8px 6px",
    textAlign: "center",
    fontWeight: 600,
    fontSize: 12,
  };

  const labelStyle: React.CSSProperties = {
    backgroundColor: "#fafafa",
    border: "1px solid #e5e7eb",
    padding: "8px 10px",
    fontWeight: 500,
    fontSize: 13,
    whiteSpace: "nowrap",
    width: "190px",
  };

  const cellStyle: React.CSSProperties = {
    border: "1px solid #e5e7eb",
    padding: "5px 6px",
    textAlign: "center",
    verticalAlign: "middle",
  };

  const subCellStyle: React.CSSProperties = {
    ...cellStyle,
    backgroundColor: "#fafafa",
  };

  const blankCellStyle: React.CSSProperties = {
    ...cellStyle,
    backgroundColor: "#f5f5f5",
    color: "#d1d5db",
    textAlign: "center",
  };

  return (
    <Modal
      title={isViewMode ? "Footpaths — Construction Configuration (View Only)" : "Footpaths — Construction Configuration"}
      open={open}
      onCancel={onCancel ?? onClose}
      onOk={onClose}
      okText="Done"
      cancelButtonProps={{ style: { display: "none" } }}
      width="min(1500px, calc(100vw - 48px))"
      styles={{ body: { padding: "16px 0" } }}
    >
      <div className={isViewMode ? "view-mode-modal-content" : ""}>
        <table style={{ borderCollapse: "collapse", tableLayout: "fixed", width: "100%" }}>
          <colgroup>
            <col style={{ width: "190px" }} />
            {COLUMN_KEYS.map((key) => (
              <col key={key} style={{ width: `calc((100% - 190px) / ${COLUMN_KEYS.length})` }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th style={{ ...labelStyle, textAlign: "left" }} />
              {COLUMN_KEYS.map((key) => (
                <th key={key} style={thStyle}>
                  {COLUMN_LABELS[key]}
                </th>
              ))}
            </tr>
            <tr>
              <th style={{ ...labelStyle, textAlign: "left" }} />
              {COLUMN_KEYS.map((key) => (
                <th
                  key={key}
                  style={{
                    ...thStyle,
                    fontSize: 11,
                    color: "#6B7280",
                    fontWeight: 400,
                    padding: "2px 8px",
                  }}
                >
                  m
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Membrane */}
            <tr>
              <td style={labelStyle}>Membrane</td>
              {COLUMN_KEYS.map((key) => (
                <td key={key} style={key === "verge" ? blankCellStyle : cellStyle}>
                  {key !== "verge" && (
                    <Select
                      value={footpathsConfig[key].membrane ? "Yes" : "No"}
                      onChange={(v) => updateCol(key, "membrane", v === "Yes")}
                      size="small"
                      style={{ width: "100%" }}
                    >
                      <Select.Option value="No">No</Select.Option>
                      <Select.Option value="Yes">Yes</Select.Option>
                    </Select>
                  )}
                </td>
              ))}
            </tr>
            {/* Capping */}
            <tr>
              <td style={labelStyle}>Capping</td>
              {COLUMN_KEYS.map((key) => (
                <td key={key} style={key === "verge" ? blankCellStyle : cellStyle}>
                  {key !== "verge" && (
                    <NumberInput
                      value={footpathsConfig[key].capping ?? 0}
                      onChange={(v) => updateCol(key, "capping", v)}
                      decimalPlaces={3}
                      size="small"
                      style={{ width: "100%", textAlign: "right" }}
                    />
                  )}
                </td>
              ))}
            </tr>
            {/* Sub Base */}
            <tr>
              <td style={labelStyle}>Sub Base</td>
              {COLUMN_KEYS.map((key) => (
                <td key={key} style={key === "verge" ? blankCellStyle : cellStyle}>
                  {key !== "verge" && (
                    <NumberInput
                      value={footpathsConfig[key].subBase ?? 0}
                      onChange={(v) => updateCol(key, "subBase", v)}
                      decimalPlaces={3}
                      size="small"
                      style={{ width: "100%", textAlign: "right" }}
                    />
                  )}
                </td>
              ))}
            </tr>
            {/* Base */}
            <tr>
              {renderCourseLabel("Base", "baseCourseType", "baseAttachments", BASE_COURSE_OPTIONS)}
              {COLUMN_KEYS.map((key) => (
                <td key={key} style={key === "verge" ? blankCellStyle : cellStyle}>
                  {key !== "verge" && (
                    <Select
                      value={footpathsConfig[key].base ?? 0}
                      onChange={(v) => updateCol(key, "base", v)}
                      size="small"
                      style={{ width: "100%" }}
                    >
                      <Select.Option value={0}>Select</Select.Option>
                      <Select.Option value={0.1}>100mm thick AC32 DB base course</Select.Option>
                      <Select.Option value={0.15}>150mm thick AC32 DB base course</Select.Option>
                    </Select>
                  )}
                </td>
              ))}
            </tr>
            {/* Binder */}
            <tr>
              {renderCourseLabel("Binder", "binderCourseType", "binderAttachments", BINDER_COURSE_OPTIONS)}
              {COLUMN_KEYS.map((key) => (
                <td key={key} style={key === "verge" ? blankCellStyle : cellStyle}>
                  {key !== "verge" && (
                    <Select
                      value={footpathsConfig[key].binder ?? 0}
                      onChange={(v) => updateCol(key, "binder", v)}
                      size="small"
                      style={{ width: "100%" }}
                    >
                      <Select.Option value={0}>Select</Select.Option>
                      <Select.Option value={0.05}>50mm thick AC20 DB binder course</Select.Option>
                      <Select.Option value={0.06}>60mm thick AC20 DB binder course</Select.Option>
                      <Select.Option value={0.075}>75mm thick AC20 DB binder course</Select.Option>
                      <Select.Option value={0.08}>80mm thick AC20 DB binder course</Select.Option>
                      <Select.Option value={0.1}>100mm thick AC20 DB binder course</Select.Option>
                    </Select>
                  )}
                </td>
              ))}
            </tr>
            {/* Surface */}
            <tr>
              <td style={labelStyle}>Surface</td>
              {COLUMN_KEYS.map((key) => (
                <td key={key} style={key === "verge" ? blankCellStyle : cellStyle}>
                  {key !== "verge" && (
                    <Select
                      value={footpathsConfig[key].surface ?? 0}
                      onChange={(v) => updateCol(key, "surface", v)}
                      size="small"
                      style={{ width: "100%" }}
                    >
                      <Select.Option value={0}>Select</Select.Option>
                      <Select.Option value={0.02}>20mm th AC6 DB surface course</Select.Option>
                      <Select.Option value={0.025}>25mm th AC6 DB surface course</Select.Option>
                      <Select.Option value={0.03}>30mm th AC6 DB surface course</Select.Option>
                    </Select>
                  )}
                </td>
              ))}
            </tr>
            {/* Construction Type (dropdown sub-row, no label) */}
            <tr>
              <td style={{ ...labelStyle, color: "#9CA3AF", fontSize: 12 }} />
              {COLUMN_KEYS.map((key) => {
                const options =
                  key === "blockPavedCrossover"
                    ? BLOCK_PAVED_CONSTRUCTION_TYPES
                    : key === "verge"
                    ? VERGE_CONSTRUCTION_TYPES
                    : TARMAC_CONSTRUCTION_TYPES;
                return (
                  <td key={key} style={subCellStyle}>
                    <Select
                      value={footpathsConfig[key].constructionType}
                      onChange={(v) => updateCol(key, "constructionType", v)}
                      size="small"
                      style={{ width: "100%" }}
                    >
                      {options.map((t) => (
                        <Select.Option key={t} value={t}>{t}</Select.Option>
                      ))}
                    </Select>
                  </td>
                );
              })}
            </tr>
            {/* Topsoil */}
            <tr>
              <td style={labelStyle}>Topsoil</td>
              {COLUMN_KEYS.map((key) => (
                <td key={key} style={key !== "verge" ? blankCellStyle : cellStyle}>
                  {key === "verge" && (
                    <NumberInput
                      value={footpathsConfig[key].topsoil ?? 0}
                      onChange={(v) => updateCol(key, "topsoil", v)}
                      decimalPlaces={3}
                      positiveOnly
                      zeroIntegerOnly
                      size="small"
                      style={{ width: "100%", textAlign: "right" }}
                    />
                  )}
                </td>
              ))}
            </tr>
            {/* Total */}
            <tr>
              <td style={{ ...labelStyle, fontWeight: 600 }}>Total (m)</td>
              {COLUMN_KEYS.map((key) => (
                <td
                  key={key}
                  style={{ ...cellStyle, backgroundColor: "#f0f9ff" }}
                >
                  <Tag
                    color="blue"
                    style={{ width: "100%", textAlign: "center", margin: 0 }}
                  >
                    {calcTotal(footpathsConfig[key]).toFixed(3)}
                  </Tag>
                </td>
              ))}
            </tr>
            {/* Average Total */}
            <tr>
              <td
                style={{
                  ...labelStyle,
                  fontWeight: 600,
                  whiteSpace: "normal",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  Average Total (m)
                  <Tooltip title="Averages only the column Totals greater than 0 — columns with a Total of 0 are excluded (Excel: AVERAGEIF(range, &quot;&gt;0&quot;)).">
                    <InfoCircleOutlined style={{ color: COLORS.primary, cursor: "help" }} />
                  </Tooltip>
                </span>
              </td>
              <td
                colSpan={COLUMN_KEYS.length}
                style={{ ...cellStyle, backgroundColor: "#f0f9ff" }}
              >
                <Tag
                  color="blue"
                  style={{ width: "100%", textAlign: "center", margin: 0 }}
                >
                  {calculateFootpathsAverageBuildUp(footpathsConfig).toFixed(3)}
                </Tag>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Modal>
  );
};

export default FootpathsConfigModal;
